import { IconButton, ProgressPointBar, ScrollbarContainer, SidebarItem } from 'UI';
import { ManualTypeEnum, PaymentDtoLevelEnum, PaymentDtoTypeEnum } from 'api/generated';
import {
  BookFillIcon,
  CloseIcon,
  DonutChartIcon,
  FlashlightIcon,
  HomeIcon,
  LayoutBottomIcon,
  MenuIcon,
  TextbookIcon,
  TodoIcon,
  UserIcon,
} from 'assets/icons';
import { accessLevels } from 'constant';
import { MAXIMUM_COURSE_POINTS } from 'constant/maximumPoints';
import { routes } from 'constant/routes';
import { useClientSize, useToggle, useToggleScroll } from 'hooks';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectors, useAppSelector } from 'store';
import styled from 'styled-components';
import { respondToHeight, respondToWidth } from 'styles/general/respondTo';
import { canUserAccessTextbook } from 'utils/reader';
import { trackAmplitudeEvent } from 'utils/trackAmplitudeEvent';

import { NoStreakModal, StreakModal, UpgradeSubscriptionModal } from '../Modals';
import StreakFireButton from '../StreakFireButton';
import StudyThisButton from '../StydyThisButton';
import { PerformancePlug } from './components';

export type SubscriptionType = 'gold' | 'silver' | 'bronze';

type SidebarProps = {
  className?: string;
};

const Sidebar: FC<SidebarProps> = ({ className }) => {
  const getOptionalTrimmedString = (obj: unknown, key: string): string | undefined => {
    if (!obj || typeof obj !== 'object') return undefined;
    const value = (obj as Record<string, unknown>)[key];
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed || undefined;
  };

  const [showSidebar, setShowSidebar] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getIsBreakpoint } = useClientSize();
  const { isOpen, close, open } = useToggle();
  const isHomePage = location?.pathname === '/home';

  const activeCourse = useAppSelector(selectors.courses.selectActiveCourse);
  const courses = useAppSelector(selectors.courses.selectCourses);
  const canShowTextbook = canUserAccessTextbook(courses, activeCourse);
  const direction = useAppSelector(selectors.direction.selectDirection);
  const coursePoints = useAppSelector(selectors.performance.selectCourseStatistic);
  const isDarkMode = useAppSelector(selectors.settings.isDarkMode);
  const paymentLevel = useAppSelector(selectors.courses.selectCurrentPaymentLevel);
  const paymentType = useAppSelector(selectors.courses.selectCurrentPaymentType);
  const streakCount = useAppSelector((store) => store.performance.courseStatistic?.streak);
  const hasNoStreak = !Number(streakCount);
  const isGoldPaymentOrTrial = paymentLevel === PaymentDtoLevelEnum.Gold || paymentType === PaymentDtoTypeEnum.Free;
  const points = coursePoints?.totalInPoints || 0;
  const courseLogoUrl = getOptionalTrimmedString(activeCourse, 'courseLogoUrl') || '';
  const courseDarkLogoUrl = getOptionalTrimmedString(activeCourse, 'courseDarkLogoUrl') || '';
  const logo = isDarkMode
    ? courseDarkLogoUrl || courseLogoUrl || direction?.darkLogoUrl
    : courseLogoUrl || direction?.logoUrl;

  const [isShowStreakModal, setIsShowStreakModal] = useState(false);

  const handleCloseStreakModal = () => {
    setIsShowStreakModal(false);
  };

  const handleOpenStreakModal = () => {
    setIsShowStreakModal(true);
  };

  const { hasPerformance } = activeCourse?.currentPayment.level
    ? accessLevels[activeCourse.currentPayment.level]
    : accessLevels.null;

  const isWidthSm = getIsBreakpoint('menu');

  useToggleScroll(isWidthSm && showSidebar);

  const disableScroll = () => {
    document.body.style.overflow = 'hidden';
  };
  const enableScroll = () => {
    document.body.style.overflow = '';
  };

  useEffect(() => (showSidebar ? disableScroll() : enableScroll()), [showSidebar]);

  useEffect(() => {
    if (!isWidthSm) {
      enableScroll();
      setShowSidebar(false);
    }
  }, [isWidthSm]);

  const handleOpenProfilePage = () => {
    navigate(routes.profile);
  };

  return (
    <Root className={className} $isShow={showSidebar} $isHomePage={isHomePage}>
      <Header>
        <LogoContainer onClick={() => navigate(routes.home)}>
          <LogoImg src={logo} />
        </LogoContainer>
        <MenuContainer>
          <IconWrapper>
            {isHomePage && isGoldPaymentOrTrial && <StyledStreakFireButton onClick={handleOpenStreakModal} />}
            {isHomePage && <StyledStudyThisButton />}
          </IconWrapper>
          {<StyledProfileButton iconComponent={<UserIcon />} onClick={handleOpenProfilePage} />}
          <StyledButton onClick={() => setShowSidebar((prev) => !prev)}>
            {showSidebar ? <CloseIcon /> : <MenuIcon />}
          </StyledButton>
        </MenuContainer>
      </Header>
      <Wrapper $show={showSidebar}>
        <SidebarItemContainer>
          <StyledSidebarItem
            title="Home"
            iconComponent={<HomeIcon />}
            isSelect={location.pathname === routes.home}
            onClick={() => navigate(routes.home)}
          />
        </SidebarItemContainer>
        {canShowTextbook && (
          // `textbook-nav` is the anchor for the Textbook onboarding step (see Home.tsx).
          <SidebarItemContainer className="textbook-nav">
            <StyledSidebarItem
              title="Textbook"
              iconComponent={<TextbookIcon />}
              isSelect={location.pathname === routes.reader}
              onClick={() => {
                navigate(routes.reader);
                trackAmplitudeEvent('Textbook Pressed');
              }}
            />
          </SidebarItemContainer>
        )}
        <SidebarItemContainer>
          <StyledSidebarItem
            title="Study guide"
            iconComponent={<BookFillIcon />}
            isSelect={location.pathname === routes.studyGuide}
            onClick={() => {
              navigate(routes.studyGuide);
              trackAmplitudeEvent('Study Guide Pressed');
            }}
          />
        </SidebarItemContainer>
        <SidebarItemContainer>
          <StyledSidebarItem
            title="Tests"
            iconComponent={<TodoIcon />}
            isSelect={location.pathname === routes.testList}
            onClick={() => {
              navigate(routes.testList);
              trackAmplitudeEvent('Tests Pressed');
            }}
          />
        </SidebarItemContainer>
        <SidebarItemContainer>
          <StyledSidebarItem
            title="Flashcards"
            iconComponent={<FlashlightIcon />}
            isSelect={location.pathname === routes.flashcardPacks}
            onClick={() => {
              navigate(routes.flashcardPacks);
              trackAmplitudeEvent('Flashcards Pressed');
            }}
          />
        </SidebarItemContainer>
        <SidebarItemContainer>
          <StyledSidebarItem
            title="Mnemonic cards"
            iconComponent={<LayoutBottomIcon />}
            isSelect={location.pathname === routes.mnemonicCardList}
            onClick={() => {
              navigate(routes.mnemonicCardList);
              trackAmplitudeEvent('Mnemonic Cards Pressed');
            }}
          />
        </SidebarItemContainer>
        {hasPerformance && (
          <SidebarItemContainer>
            <StyledSidebarItem
              title="Performance"
              iconComponent={<DonutChartIcon />}
              isSelect={location.pathname === routes.performance}
              onClick={() => {
                navigate(routes.performance);
                trackAmplitudeEvent('Performance Pressed');
              }}
            />
          </SidebarItemContainer>
        )}
        <ProgressBarContainer $hasPerformance={hasPerformance}>
          {activeCourse &&
            (hasPerformance ? (
              <ProgressPointBar points={points} total={MAXIMUM_COURSE_POINTS} />
            ) : (
              <PerformancePlug onClick={open} />
            ))}
        </ProgressBarContainer>
      </Wrapper>
      <UpgradeSubscriptionModal isOpen={isOpen} onClose={close} manualType={ManualTypeEnum.PerformanceUpgrade} />
      {isShowStreakModal && hasNoStreak ? (
        <NoStreakModal isModalVisible={isShowStreakModal} setModalVisible={handleCloseStreakModal} />
      ) : (
        <StreakModal isOpen={isShowStreakModal} onClose={handleCloseStreakModal} />
      )}
    </Root>
  );
};

export default Sidebar;

const Root = styled.div<{ $isShow: boolean; $isHomePage?: boolean }>`
  width: 148px;
  height: 100%;
  max-height: 100vh;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 28px 12px 28px 20px;
  background-color: ${({ theme: { colors } }) => colors.neutrals[11]};
  align-self: start;

  ${respondToHeight.ls`
    padding: 5px 12px 5px 20px;
  `}

  ${respondToWidth.menu`
    width: 100%;
    min-width: 0;
    height: 65px;

    z-index: 1001;

    padding: 5px 16px 0px 16px;
    align-items: start;
    justify-content: initial;
  `};

  ${respondToWidth.s`
    padding-inline: 12px;
  `}

  ${({ $isHomePage }) => respondToWidth.s`
    height:${$isHomePage ? '65px' : 'unset'}
  `}
`;

const Wrapper = styled(ScrollbarContainer)<{ $show: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  width: 100%;

  ${({ $show, theme: { colors } }) => respondToWidth.menu`
    width: 100%;
    height: calc(100% - 60px);

    position: absolute;
    display:${$show ? 'flex' : 'none'};

    margin-top: 60px;

    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    z-index: 1000;

    background-color: ${colors.neutrals[11]};


    align-items: start;
    padding: 20px 16px;
  `}
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 16px;
  cursor: pointer;
  /* Root is 148px wide with 20px/12px side padding, so 116px is exactly its content box.
     Without this cap a wide brand logo sizes to its own aspect ratio at max-height 40px and
     spills past the sidebar edges. border-box is global, so the 8px below is included. */
  max-width: 116px;
  padding-right: 8px;

  svg {
    width: 68px;
    height: 32px;
  }

  ${respondToWidth.menu`
    width:68px;
    height: 32px;
    padding:0;
    margin:0;

    ${respondToHeight.lg`
    margin-bottom:0;
  `}
  `}

  ${respondToWidth.s`
    width: 56px;
    height: 26px;

    svg {
      width: 56px;
      height: 26px;
    }
  `}
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${respondToWidth.menu`
    justify-content: space-between;
    min-width: 0;
    padding-top: 10px;
    padding-bottom: 10px;
  `}
`;

const SidebarItemContainer = styled.div`
  width: 100%;

  ${({ theme: { colors } }) => respondToWidth.menu`
  border-top: 1px solid ${colors.neutrals[9]};
  &:first-of-type {
    border: none;
  }
  `}
`;

const IconWrapper = styled.div`
  display: flex;

  ${respondToWidth.s`
    position: static;
    bottom: auto;
    right: auto;
    align-items: center;
  `}
`;

const StyledSidebarItem = styled(SidebarItem)`
  ${respondToHeight.sm`
    padding: 10px 0;
  `}

  ${respondToWidth.menu`
    width:100%;
    padding: 20px 0;
  `}
`;

const ProgressBarContainer = styled.div<{ $hasPerformance: boolean }>`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const StyledButton = styled.button`
  display: none;
  background-color: inherit;
  outline: none;
  border: none;
  padding: 0;
  height: 32px;
  flex-shrink: 0;

  ${respondToWidth.s`
    height: 28px;

    & svg {
      width: 26px;
      height: 26px;
    }
  `}

  ${respondToWidth.menu`
  display:block;
`}
`;

const LogoImg = styled.img`
  max-width: 100%;
  max-height: 40px;
`;

const MenuContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
  flex-shrink: 1;

  ${respondToWidth.s`
  position: relative;
  `}
`;

const StyledStudyThisButton = styled(StudyThisButton)`
  display: none;
  width: 137px;
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
  margin-right: 8px;

  ${respondToWidth.menu`
    display: flex !important;
  `}

  ${respondToWidth.s`
    width: 112px;
    height: 30px;
    padding: 4px 12px 4px 10px;
    font-size: 12px;
    margin-right: 6px;

    & svg {
      width: 16px;
      height: 16px;
      margin-left: 6px;
    }
  `}
`;

const StyledStreakFireButton = styled(StreakFireButton)`
  display: none !important;
  flex-shrink: 0;
  margin-right: 8px;

  ${respondToWidth.menu`
    display: flex !important;
  `}

  ${respondToWidth.s`
    min-width: 50px;
    max-width: 50px;
    height: 30px;
    margin-right: 6px;

    & svg {
      width: 16px;
      height: 16px;
    }

    & p {
      font-size: 12px;
      margin-left: 3px;
    }
  `}
`;

const StyledProfileButton = styled(IconButton)`
  max-height: 36px;
  display: none !important;
  flex-shrink: 0;
  margin-right: 8px;

  ${respondToWidth.menu`
    display: flex !important;
  `}

  ${respondToWidth.s`
    min-width: 32px;
    min-height: 32px;
    max-height: 32px;
    padding: 3px;
    margin-right: 6px;

    & svg {
      width: 20px;
      height: 20px;
    }
  `}
`;
